import { VehicleSection } from '@/components/VehicleSection/VehicleSection';
import { HowItWorksCTA } from '@/reports/shared/ReportSection/components';
import { AutomobileSales } from '@/reports/types/vehicles';
import {
  Icons,
  Text,
  Link,
  Stack,
  Table,
  useMediaQuery,
  useTheme,
} from '@/theme/index';
import { capitalizeString } from '@/utils/capitalizeString';
import { commaFormat } from '@/utils/commaFormat/commaFormat';
import { formatDateFromReport } from '@/utils/formatDateFromReport/formatDateFromReport';
import { commaFormatCurrency } from '@/utils/index';

export interface VehicleSalesProps {
  /** A list of all sales data */
  vehicleSalesList: AutomobileSales;
}

interface DateCellProps {
  /** Sale date */
  salesDate: string | null;
  /** Sale source */
  salesSource: string | null;
  salesUrl: string;
  salesId: string;
  sellerName: string;
  sellerType: string;
}

function HowItWorksCTAContent() {
  return (
    <Text
      variant="body2"
      fontSize="0.9rem"
      color={(theme) => theme.palette.grey[800]}
    >
      Sales listings data is aggregated from over 50,000 dealerships and
      websites nationwide. It may include private listing and auction listings
      on Copart and eBay.
    </Text>
  );
}

/** It renders a table view of sales listings */
export const VehicleSales: React.FC<VehicleSalesProps> = ({
  vehicleSalesList,
}) => {
  const theme = useTheme();

  const header = ['Date', 'Type', 'Price', 'Mileage', 'Days on Market'].map(
    (cell) => {
      return (
        <Text variant="h5" sx={{ p: 1, m: 0 }}>
          {cell}
        </Text>
      );
    }
  );

  const modifiedSalesList = vehicleSalesList.map((sale) => {
    let period = '';
    const id = sale?.id || '';
    const salesSource = sale?.source || '';
    const sellerName = sale?.seller?.name || '';
    const sellerType = sale?.seller?.type || '';
    const url = sale?.listing_url || '';
    const type = sale?.inventory_type
      ? capitalizeString(sale?.inventory_type)
      : '';
    const listingPrice = sale?.listing_price
      ? commaFormatCurrency(sale?.listing_price)
      : '';
    const priceChangeIncrease =
      sale?.history?.price_change_pct && sale?.history?.price_change_pct > 0;
    const PriceChangeIcon =
      Icons[priceChangeIncrease ? 'ArrowUpward' : 'ArrowDownward'];
    const priceChangePct = Math.abs(sale?.history?.price_change_pct || 0);
    const HistoryMileageIcon =
      Icons[
        sale?.history?.mileage_change && sale?.history?.mileage_change > 0
          ? 'Add'
          : 'Remove'
      ];
    const historyMileage =
      sale?.history?.mileage_change &&
      `${commaFormat(Math.abs(sale?.history?.mileage_change || 0))} miles`;
    const mileage = sale?.vehicle?.mileage
      ? `${commaFormat(parseInt(sale?.vehicle?.mileage))} miles`
      : '';
    const daysOnMarket = sale?.history?.days_on_market
      ? `${sale?.history?.days_on_market} days`
      : '';

    if (sale.meta) {
      const first = formatDateFromReport(
        sale.meta?.first_seen_date?.full,
        'MM/dd/yyyy'
      );
      const last =
        sale.meta?.last_seen_date?.full &&
        formatDateFromReport(sale.meta?.last_seen_date?.full, 'MM/dd/yyyy');

      period = [first, last].filter((date) => Boolean(date)).join(' - ');
    } else if (sale.date?.full) {
      period = formatDateFromReport(sale?.date.full, 'MM/dd/yyyy');
    }

    return [
      <DateCell
        salesDate={period}
        salesUrl={url}
        salesSource={salesSource}
        salesId={id}
        sellerName={sellerName}
        sellerType={sellerType}
      />,
      <Text variant="body2" sx={{ p: theme.spacing(1) }}>
        {type}
      </Text>,
      <Stack>
        <Text variant="body2" sx={{ p: theme.spacing(0) }}>
          {listingPrice}
        </Text>
        {Boolean(priceChangePct) && (
          <Text
            variant="body2"
            sx={{ p: theme.spacing(0) }}
            display="flex"
            alignItems="center"
            color={priceChangeIncrease ? 'success.main' : 'error.main'}
          >
            <PriceChangeIcon
              fontSize="small"
              color={priceChangeIncrease ? 'primary' : 'error'}
              sx={{ width: '1rem', height: '1rem' }}
            />
            {priceChangePct}%
          </Text>
        )}
      </Stack>,
      <Stack direction="column">
        <Text variant="body2" sx={{ p: theme.spacing(1) }}>
          {mileage}
        </Text>
        {Boolean(historyMileage) && (
          <Text
            variant="body2"
            sx={{ p: theme.spacing(1) }}
            display="flex"
            alignItems="center"
          >
            <HistoryMileageIcon fontSize="small" color="primary" />
            {historyMileage}
          </Text>
        )}
      </Stack>,
      <Text variant="body2" sx={{ p: theme.spacing(1) }}>
        {daysOnMarket}
      </Text>,
    ];
  });

  const modifiedMobileSalesList = vehicleSalesList?.map((sale, index) => {
    let period = '';
    const id = sale?.id || '';
    const salesSource = sale?.source || '';
    const sellerName = sale?.seller?.name || '';
    const sellerType = sale?.seller?.type || '';
    const url = sale?.listing_url || '';
    const type = sale?.inventory_type
      ? capitalizeString(sale?.inventory_type)
      : '';
    const price = sale?.listing_price
      ? commaFormatCurrency(sale?.listing_price)
      : '';
    const mileage = sale?.vehicle?.mileage
      ? `${commaFormat(parseInt(sale?.vehicle?.mileage))} miles`
      : '';
    const daysOnMarket = sale?.history?.days_on_market
      ? `${sale?.history?.days_on_market} days`
      : '';

    if (sale.meta) {
      const first = formatDateFromReport(
        sale.meta?.first_seen_date?.full,
        'MM/dd/yyyy'
      );
      const last =
        sale.meta?.last_seen_date?.full &&
        formatDateFromReport(sale.meta?.last_seen_date?.full, 'MM/dd/yyyy');

      period = [first, last].filter((date) => Boolean(date)).join(' - ');
    } else if (sale.date?.full) {
      period = formatDateFromReport(sale?.date.full, 'MM/dd/yyyy');
    }

    return (
      <Table
        key={index}
        className="striped mobile"
        headers={[
          <DateCell
            salesDate={period}
            salesUrl={url}
            salesSource={salesSource}
            salesId={id}
            sellerName={sellerName}
            sellerType={sellerType}
          />,
        ]}
        rows={[
          ['Type', type],
          ['Price', price],
          ['Mileage', mileage],
          ['Days on Market', daysOnMarket],
        ]}
      />
    );
  });
  return (
    <VehicleSection
      headers={header}
      tableData={modifiedSalesList}
      mobileTableData={modifiedMobileSalesList}
      reportSectionTitle="Possible Sale Listings"
      emptySectionTitle="possible sale listings"
      sectionId="vehicle-sales"
      tableTitle="Listing History"
      howItWorks={<HowItWorksCTA content={HowItWorksCTAContent()} />}
    />
  );
};

/** It renders stack view of the date and source */
const DateCell = ({
  salesDate,
  salesUrl,
  salesSource,
  sellerName,
  salesId,
  sellerType,
}: DateCellProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Stack
      id={salesId}
      data-testid="sales-table-item"
      direction="column"
      sx={{ p: theme.spacing(1) }}
    >
      <Text
        variant={isMobile ? 'body1' : 'body2'}
        sx={{
          fontWeight: isMobile ? '700' : '400',
        }}
      >
        {salesDate}
      </Text>
      <Text variant={isMobile ? 'body1' : 'body2'}>
        {salesSource ? (
          <>
            <Link
              href={salesUrl}
              target="_blank"
              rel="noopener"
              color="success.main"
            >
              {salesSource}
            </Link>
            {sellerType ? ` (${sellerType})` : ''}
          </>
        ) : (
          sellerName
        )}
      </Text>
    </Stack>
  );
};
